<template>
  <div class="dates text-dark">
    <div class="dates__header">
      <ChangeSchoolYear :whiteTheme="true" class="fit-content dates__change-schoolyear"></ChangeSchoolYear>
      <div class="dates-input-group">
        <label>Select the start and end dates for insertion</label>
        <Datepickerrange
          :startdate="startDate"
          :enddate="endDate"
          @startchanged="setStartDate"
          @endchanged="setEndDate">
        </Datepickerrange>
      </div>
    </div>
    <Loader v-if="isLoading" bg-color="#fff"></Loader>
    <div class="dates-input-group marg-1-top" v-else>
      <p class="text-explainer"><b>Hint:</b> Click on a day to exclude/include day in rotation</p>
        <div class="cal-month" v-for="(monthData, month) in groupedDatesByMonth" :key="month">
          <h3>{{ month }}</h3>
          <ul class="cal-month__header">
            <li>Mon</li>
            <li>Tue</li>
            <li>Wed</li>
            <li>Thu</li>
            <li>Fri</li>
            <li>Sat</li>
            <li>Sun</li>
          </ul>
          <ul class="cal-month__body">
            <li v-for="n in monthData.emptyDays">&nbsp;</li>
            <li class="cal-month__body__day"
                v-for="(dateObj, i) in monthData.dates" :key="i"
                @click="toggleDate(dateObj)"
                :content="dateObj.isWeekend ? 'Weekend' : dateObj.isVacationDay ? 'Vacation day' : dateObj.isIncluded ? 'Exclude day in rotation' : 'Include day in rotation'"
                v-tippy="{
                    arrow: true,
                    zIndex: 123123123123
                  }"
                :class="[
                  {'cal-month__body__day--can-be-toggled': !dateObj.isVacationDay && !dateObj.isWeekend},
                  {'cal-month__body__day--holiday': dateObj.isVacationDay},
                  {'cal-month__body__day--inactive': !dateObj.isIncluded},
                  {'cal-month__body__day--is-weekend': dateObj.isWeekend}
                  ]">
              <p class="cal-month__body__date">{{ dateObj.formattedDate }}</p>
              <p class="cal-month__body__date__to-insert" v-if="dateObj.dayToInsert">{{ dateObj.dayToInsert.label }}</p>
            </li>
          </ul>
        </div>
    </div>
  </div>
</template>

<script>
import Datepickerrange from '../datepicker/DatePickerRange'
import ChangeSchoolYear from '../School/ChangeSchoolYear'
import moment from 'moment'
import docendo from '../../docendo'
import Loader from '../common/Loader'

export default {
  components: {Loader, ChangeSchoolYear, Datepickerrange},
  props: {
    daysToCopy: {
      type: Array,
      default: () => []
    },
    schoolId: {
    },
    initStartDate: {
      default: () => moment()
    }
  },
  data () {
    return {
      startDate: moment(),
      endDate: moment().add(1, 'days'),
      vacationsDays: [],
      datesBetween: [],
      excludedDates: [],
      isLoading: true
    }
  },
  computed: {
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    schoolYearId () {
      return this.activeSchoolYear.id
    },
    schoolSettings () {
      return this.$store.state.schoolSettings
    },
    groupedDatesByMonth () {
      const groupedDates = {}

      this.datesWithMeta.forEach(dateObj => {
        const month = moment(dateObj.date).format('MMMM YYYY')
        if (!groupedDates[month]) {
          groupedDates[month] = {
            dates: [],
            emptyDays: 0
          }
          if (groupedDates[month].emptyDays < 0) {
            groupedDates[month].emptyDays = 6
          }
          let date = moment(dateObj.date)
          let startOfWeek = date.clone().startOf('isoWeek')
          let days = date.diff(startOfWeek, 'days')
          groupedDates[month].emptyDays = days
        }
        groupedDates[month].dates.push(dateObj)
      })

      return groupedDates
    },
    datesWithMeta () {
      let daysToCopyIndex = 0

      return this.datesBetween.map(dateObj => {
        const dateMoment = moment(dateObj.date)
        const isWeekend = dateMoment.day() === 6 || dateMoment.day() === 0
        const isVacationDay = this.vacationsDays.includes(dateObj.date)
        const isExcluded = this.excludedDates.includes(dateObj.date)
        const isIncluded = !isWeekend && !isVacationDay && !isExcluded

        let dayToInsert = null
        if (isIncluded) {
          dayToInsert = this.daysToCopy[daysToCopyIndex]
          daysToCopyIndex = (daysToCopyIndex + 1) % this.daysToCopy.length
        }

        return {
          ...dateObj,
          isWeekend,
          isVacationDay,
          isIncluded,
          dayToInsert
        }
      })
    }
  },
  watch: {
    activeSchoolYear () {
      this.onSchoolYearChanged()
    },
    datesWithMeta () {
      this.$emit('pasteDates', this.datesWithMeta)
    }
  },
  methods: {
    toggleDate (dateObj) {
      const idx = this.excludedDates.indexOf(dateObj.date)
      if (idx < 0) {
        this.excludedDates.push(dateObj.date)
      } else {
        this.excludedDates.splice(idx, 1)
      }
      this.saveExcludedDates(this.excludedDates)
    },
    saveExcludedDates (val) {
      const propName = 'rotating_schedules_excluded_dates'
      const saveObj = {[propName]: JSON.stringify(val)}
      this.$store.dispatch('updateSchoolSettingsForSchoolYear', saveObj)
    },
    getExcludedDates () {
      const settings = this.schoolSettings
      const schoolYearId = this.schoolYearId
      const propSchoolYear = `school_year_${schoolYearId}`
      const propName = 'rotating_schedules_excluded_dates'
      if (settings && settings[propSchoolYear] && settings[propSchoolYear][propName]) {
        const arr = JSON.parse(settings[propSchoolYear][propName])
        this.excludedDates = arr
      }
    },
    createDatesBetween () {
      const start = moment(this.startDate)
      const end = moment(this.endDate)
      const dates = []

      while (start.isSameOrBefore(end)) {
        dates.push({
          date: start.format('YYYY-MM-DD'),
          formattedDate: start.format('D')
        })
        start.add(1, 'days')
      }
      this.datesBetween = dates
    },
    setStartDate (date) {
      this.setDates(date, null)
    },
    setEndDate (date) {
      this.setDates(null, date)
    },
    setDates (start, end) {
      if (start) {
        this.startDate = start
      }
      if (end) {
        this.endDate = end
      }
      this.createDatesBetween()
    },
    readVacationDays () {
      docendo.schoolVacationDaysRead(this.schoolId, success => {
        this.vacationsDays = success.map(day => day.day)
        this.isLoading = false
      }, failure => {
        console.log('failure')
      })
    },
    onSchoolYearChanged () {
      if (this.activeSchoolYear) {
        this.setDates(this.activeSchoolYear.student_time_start, this.activeSchoolYear.student_time_end)
      }
    },
    initStartAndEndDate () {
      if (this.initStartDate && this.activeSchoolYear) {
        this.setDates(this.initStartDate, this.activeSchoolYear.student_time_end)
      }
    }
  },
  beforeMount () {
    this.initStartAndEndDate()
  },
  created () {
    this.readVacationDays()
    this.getExcludedDates()
  }
}
</script>

<style scoped>
.fit-content {
  width: fit-content;
}

.cal-month {
  display: flex;
  flex-direction: column;
}

.cal-month__header {
  display: flex;
  margin: 0;
}

.cal-month__body {
  display: flex;
  flex-wrap: wrap;
  margin: 0;
}

.cal-month__header li {
  background-color: #f7f8fa;
}
.cal-month__header li,
.cal-month__body li {
  width: calc(100% / 7);
  border: 1px solid #eceef1;
  text-align: center;
  border-width: 0 1px 1px 0;
}

.cal-month__header {
  border: 1px solid #eceef1;
  border-width: 1px 0 0 1px;
}

.cal-month__body {
  border-left: 1px solid #eceef1;
}

.cal-month__body li {
  height: 45px;
}

.cal-month h3 {
  border: 1px solid #eceef1;
  border-width: 1px 1px 0 1px;
  text-align: center;
  background-color: #f7f8fa;
}

.cal-month__body__date {
  display: flex;
  justify-content: flex-end;
  padding-right: 0.5rem;
}

.cal-month__body__date__to-insert {
  background: #fefce8;
  font-size: 10px;
  color: #2b2b28;
  border: 1px solid rgb(202 138 4 / 0.2);
  display: inline-block;
  border-radius: 4px;
  padding: 4px 8px;
  line-height: 10px;
}

.cal-month__body__day {
  cursor: pointer;
}

.cal-month__body__day--inactive {
  background: #f6f7f8;
}

.dates__header {
  position: relative;
}

.dates__change-schoolyear {
  float: right;
}

.cal-month__body__day--holiday.cal-month__body__day--inactive {
  background-color: rgba(70, 138, 215, 0.2);
  cursor: not-allowed;
  opacity: 0.55;
}

.cal-month__body__day--is-weekend {
  opacity: 0.55;
  cursor: not-allowed;
}

.cal-month__body__day.cal-month__body__day--can-be-toggled {
  transition: background-color 0.25s;
}

.cal-month__body__day.cal-month__body__day--can-be-toggled:hover {
  background-color: #eee;
}
</style>



// WEBPACK FOOTER //
// CopyCalendarRotateDates.vue?86045938